import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Video, BookOpen, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';

export default function DripViewer() {
  const { accessId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('learnable_user')) return navigate('/login');

    api('/api/me/drip-content').then(({ drip_items }) => {
      const found = drip_items.find((d) => d.access_id === accessId);
      if (!found) return navigate('/dashboard');
      setItem(found);
      api(`/api/me/drip-content/${accessId}/view`, { method: 'POST' }).catch(() => {});
    }).finally(() => setLoading(false));
  }, [accessId, navigate]);

  if (loading) return <p className="max-w-4xl mx-auto px-4 py-10 text-slate-500">Loading…</p>;
  if (!item) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Link to="/dashboard" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>

      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand mb-1">
          Follow-up · {item.course_title}
        </p>
        <h1 className="text-2xl font-bold text-slate-900">{item.title}</h1>
      </div>

      {item.content_type === 'scorm' && item.scorm_folder_name && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ height: '75vh' }}>
          <iframe
            src={`/api/learning/drip-assets/${encodeURIComponent(item.scorm_folder_name)}/${item.scorm_entry_point}`}
            className="w-full h-full border-0"
            title={item.title}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      )}

      {item.content_type === 'video' && item.content_url && (
        <div className="bg-black rounded-xl overflow-hidden">
          {/youtube\.com|youtu\.be|vimeo\.com/.test(item.content_url) ? (
            <div className="aspect-video">
              <iframe
                src={toEmbedUrl(item.content_url)}
                className="w-full h-full border-0"
                title={item.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <video controls className="w-full" style={{ maxHeight: '75vh' }}>
              <source src={item.content_url} />
            </video>
          )}
        </div>
      )}

      {item.content_type === 'pdf' && item.content_url && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col" style={{ height: '80vh' }}>
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <FileText size={16} /> PDF Document
            </div>
            <a
              href={item.content_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-brand font-semibold hover:underline inline-flex items-center gap-1"
            >
              Open in new tab <ExternalLink size={13} />
            </a>
          </div>
          <iframe
            src={item.content_url}
            className="flex-1 w-full border-0"
            title={item.title}
          />
        </div>
      )}

      {!item.content_url && item.content_type !== 'scorm' && (
        <div className="text-center py-20 bg-slate-50 rounded-xl border border-slate-200">
          <BookOpen size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Content is being prepared. Check back soon.</p>
        </div>
      )}
    </div>
  );
}

function toEmbedUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com') && u.searchParams.has('v')) {
      return `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
    }
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* return as-is */ }
  return url;
}
